/**
 * Pages flow cache: persist navigation shell in localStorage, never stale execute output.
 */

import {
  isFreshEntryConfigKey,
  isTruthyPagesFlag,
  readPagesFeatureFlags,
} from "./pagesFeatureConfig";

const hasMeaningfulPagesRuntime = (pageDataEntry) => {
  const pages = pageDataEntry?.pages;
  if (!pages || typeof pages !== "object") {
    return false;
  }
  const checkObj = (obj) => {
    if (!obj || typeof obj !== "object") {
      return false;
    }
    return Object.values(obj).some((raw) => {
      if (raw == null) return false;
      if (typeof raw === "string") return raw.trim() !== "" && raw.toLowerCase() !== "null";
      if (Array.isArray(raw)) return raw.length > 0;
      if (typeof raw === "object") {
        if (raw.value != null && String(raw.value).trim() !== "") return true;
        if (raw.selected_value != null && String(raw.selected_value).trim() !== "") {
          return true;
        }
        return Object.keys(raw).length > 0;
      }
      return true;
    });
  };
  return checkObj(pages.params) || checkObj(pages.response);
};

/**
 * Fresh-entry strip on route mount — but not when loadPagesConfigAction already
 * hydrated this key (Add action → onload execute → /pages/:key).
 */
/** Edit/Clone DIRECT seed — do not clear_runtime wipe mapped shipment fields. */
const hasDirectEditOrCloneSeed = (pageDataEntry = {}) => {
  const pages = pageDataEntry?.pages || {};
  const params = pages.params || {};
  const response = pages.response || pageDataEntry?.outputVariables || {};
  const readFlag = (key) =>
    params[key] !== undefined && params[key] !== null && params[key] !== ""
      ? params[key]
      : response[key];
  return (
    isTruthyPagesFlag(readFlag("is_edit")) ||
    isTruthyPagesFlag(readFlag("pages_edit_mode")) ||
    isTruthyPagesFlag(readFlag("is_clone")) ||
    isTruthyPagesFlag(readFlag("pages_clone_mode")) ||
    String(readFlag("pages_mode") || "")
      .trim()
      .toLowerCase() === "edit" ||
    String(readFlag("pages_mode") || "")
      .trim()
      .toLowerCase() === "clone"
  );
};

export const shouldResetPagesRuntimeOnFreshEntry = (
  configKey,
  pageDataEntry,
  search = "",
  pageFlows = {}
) => {
  if (!configKey || !isFreshEntryConfigKey(configKey, pageDataEntry, search)) {
    return false;
  }
  try {
    const params = new URLSearchParams(
      typeof search === "string" ? search : ""
    );
    if (
      isTruthyPagesFlag(params.get("clearRuntime")) ||
      isTruthyPagesFlag(params.get("freshEntry"))
    ) {
      return true;
    }
  } catch (e) {
    // ignore
  }
  const entry = pageDataEntry || pageFlows?.pageData?.[configKey];
  // Keep action-button DIRECT seed (Edit/Clone → Add shipment). clear_runtime is
  // for blank Create opens, not wiping mapped clone/edit field values.
  if (hasDirectEditOrCloneSeed(entry)) {
    return false;
  }
  const activeKey = pageFlows?.activePage || pageFlows?.activeWindowPage;
  if (
    activeKey === configKey &&
    hasMeaningfulPagesRuntime(entry)
  ) {
    return false;
  }
  return true;
};

/**
 * True when route entry must not reuse prior tab session params/response.
 * Driven by ?clearRuntime=1, page flags, or session registry (from SQL flags) — not api_name lists.
 */
export const isPagesFreshEntryConfig = (configKey, pageDataEntry, search) =>
  isFreshEntryConfigKey(configKey, pageDataEntry, search);

/**
 * Keys to strip on a fresh entry visit (host + related page configs from DB flags).
 */
export const collectFreshEntryResetKeys = (
  configKey,
  pageDataEntry,
  search = ""
) => {
  if (!configKey) {
    return [];
  }
  if (!isFreshEntryConfigKey(configKey, pageDataEntry, search)) {
    return [String(configKey)];
  }
  const flags = readPagesFeatureFlags(pageDataEntry || {});
  const keys = new Set([String(configKey)]);
  if (pageDataEntry?.id) {
    keys.add(String(pageDataEntry.id));
  }
  if (Array.isArray(flags.register_fresh_entry_keys)) {
    flags.register_fresh_entry_keys.forEach((k) => {
      if (k != null && String(k).trim() !== "") {
        keys.add(String(k));
      }
    });
  }
  return [...keys];
};

/**
 * Resolve all pageData keys that alias the same config (id / api_name / map key).
 * Used to strip stale params/response before reopen or popup close.
 */
export const collectPageDataKeysForConfig = (pageFlows, configId) => {
  if (configId == null || configId === "") {
    return [];
  }
  const id = String(configId);
  const keys = new Set([id]);
  const data = pageFlows?.pageData || {};
  Object.keys(data).forEach((k) => {
    const entry = data[k];
    if (
      k === id ||
      String(entry?.id || "") === id ||
      String(entry?.configuration_api_name || "") === id
    ) {
      keys.add(String(k));
      if (entry?.configuration_api_name) {
        keys.add(String(entry.configuration_api_name));
      }
      if (entry?.id) {
        keys.add(String(entry.id));
      }
    }
  });
  return [...keys];
};

export const stripPagesRuntimeFromPageDataEntry = (
  entry = {},
  options = {}
) => {
  if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
    return {};
  }
  const { stripAutomationHostMarkup = false } = options;
  const next = { ...entry };
  if (next.pages && typeof next.pages === "object") {
    let pageShell = next.pages;
    if (stripAutomationHostMarkup && next.config_type === "AUTOMATION") {
      const { params, response, html, css, js, ...rest } = pageShell;
      pageShell = { ...rest };
    } else {
      const { params, response, ...rest } = pageShell;
      pageShell = { ...rest };
    }
    next.pages = pageShell;
  }
  if (stripAutomationHostMarkup && next.config_type === "AUTOMATION") {
    if (next.type === "PAGES") {
      delete next.type;
    }
  }
  delete next.outputVariables;
  return next;
};

export const stripPagesRuntimeFromPageFlows = (pageFlows = {}) => {
  if (!pageFlows || typeof pageFlows !== "object") {
    return {};
  }
  const next = { ...pageFlows };
  if (next.pageData && typeof next.pageData === "object") {
    const pageData = {};
    Object.keys(next.pageData).forEach((key) => {
      pageData[key] = stripPagesRuntimeFromPageDataEntry(next.pageData[key]);
    });
    next.pageData = pageData;
  }
  return next;
};

/**
 * Merge config/HTML shell from API reload without carrying prior params/response forward.
 */
export const mergePagesShellWithoutRuntime = (existing = {}, incoming = {}) => {
  const existingParams = existing?.pages?.params;
  const existingResponse = existing?.pages?.response;
  const base = stripPagesRuntimeFromPageDataEntry(existing);
  const incShell = stripPagesRuntimeFromPageDataEntry(incoming);
  const merged = {
    ...base,
    ...incShell,
  };
  merged.pages = {
    ...(base.pages || {}),
    ...(incShell.pages || {}),
  };
  if (incoming?.pages?.params != null) {
    merged.pages.params = incoming.pages.params;
  } else if (existingParams != null) {
    merged.pages.params = existingParams;
  }
  if (incoming?.pages?.response != null) {
    merged.pages.response = incoming.pages.response;
  } else if (existingResponse != null) {
    merged.pages.response = existingResponse;
  }
  if (incoming?.outputVariables != null) {
    merged.outputVariables = incoming.outputVariables;
  }
  return merged;
};

export const persistPageFlowsForBrowserStorage = (pageFlows) => {
  try {
    const stripped = stripPagesRuntimeFromPageFlows(pageFlows);
    localStorage.setItem("pageFlows", JSON.stringify(stripped));
  } catch (e) {
    // quota / private mode
  }
};

export const removePageFlowsBrowserStorage = () => {
  try {
    localStorage.removeItem("pageFlows");
  } catch (e) {
    // ignore
  }
};
